import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { 
  CheckCircle, 
  Play, 
  Clock, 
  Activity, 
  Cpu, 
  Plus, 
  Trash2, 
  ChevronRight, 
  Info,
  Calendar,
  X,
  Search
} from 'lucide-react';
import api from '../utils/api';
import CustomDropdown from '../components/CustomDropdown';
import MultiSelect from '../components/MultiSelect';
import { useToast } from '../context/ToastContext';

export default function Evaluations({ activeAiAgentId }) {
  const { showToast } = useToast();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [runningSuite, setRunningSuite] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  
  const [availableTools, setAvailableTools] = useState([]);
  const [humanEscalationEnabled, setHumanEscalationEnabled] = useState(true);
  
  // Wizard states
  const [showAddModal, setShowAddModal] = useState(false);
  const [testCases, setTestCases] = useState([]);
  const [newCase, setNewCase] = useState({ 
    name: '', 
    prompt: '', 
    expected: '', 
    eval_type: 'judge', 
    expected_tool_calls: [] 
  });

  const fetchHistory = useCallback(async () => {
    if (!activeAiAgentId) return;
    setLoading(true);
    try {
      api.setAiAgentId(activeAiAgentId);
      const data = await api.get('/api/evals/history', { aiAgentId: activeAiAgentId });
      setRuns(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch evaluations history:', err);
      showToast('Failed to load evaluation history', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId, showToast]);

  const fetchToolsAndConfig = useCallback(async () => {
    if (!activeAiAgentId) return;
    try {
      api.setAiAgentId(activeAiAgentId);
      const [toolsResponse, configData] = await Promise.all([
        api.get('/api/tools', { aiAgentId: activeAiAgentId }),
        api.getWidgetConfig().catch(() => null)
      ]);
      setAvailableTools(toolsResponse?.tools || []);
      if (configData) {
        setHumanEscalationEnabled(configData.human_escalation !== false);
      }
    } catch (err) {
      console.error('Failed to fetch agent tools or config:', err);
      showToast('Failed to fetch agent tools or config', 'error');
    }
  }, [activeAiAgentId, showToast]);

  useEffect(() => {
    setSelectedRun(null);
    if (activeAiAgentId) {
      fetchHistory();
      fetchToolsAndConfig();
    }
  }, [activeAiAgentId, fetchHistory, fetchToolsAndConfig]);

  const handleRunEvaluations = async () => {
    if (testCases.length === 0) {
      showToast('Please add at least one test case before running', 'error');
      return;
    }
    setRunningSuite(true);
    try {
      const payload = {
        ai_agent_id: activeAiAgentId,
        test_cases: testCases
      };
      const res = await api.post('/api/evals/run', payload, { aiAgentId: activeAiAgentId });
      await fetchHistory();
      if (res) {
        setSelectedRun(res);
      }
      setShowAddModal(false);
      showToast('Evaluation suite completed successfully');
    } catch (err) {
      console.error('Failed to execute evaluation suite:', err);
      showToast(err.message || 'Failed to execute evaluation suite', 'error');
    } finally {
      setRunningSuite(false);
    }
  };

  const handleAddCase = () => {
    if (!newCase.name.trim() || !newCase.prompt.trim()) {
      showToast('Please provide both case name and prompt input', 'error');
      return;
    }
    if (newCase.eval_type !== 'reliability' && !newCase.expected.trim()) {
      showToast('Please provide expected output criteria', 'error');
      return;
    }
    setTestCases([...testCases, { 
      ...newCase, 
      id: Date.now() 
    }]);
    setNewCase({ name: '', prompt: '', expected: '', eval_type: 'judge', expected_tool_calls: [] });
    showToast('Test case added');
  };

  const handleRemoveCase = (id) => {
    setTestCases(testCases.filter(c => c.id !== id));
    showToast('Test case removed');
  };

  // Filter runs for current AI Agent with search and status filter - O(N)
  const filteredRuns = useMemo(() => {
    let result = runs.filter(r => String(r.ai_agent_id) === String(activeAiAgentId));

    if (statusFilter !== 'ALL') {
      result = result.filter(r => (r.summary?.status || '').toUpperCase() === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(r => 
        String(r.id).toLowerCase().includes(q) ||
        r.cases?.some(c => c.name?.toLowerCase().includes(q) || c.prompt?.toLowerCase().includes(q))
      );
    }

    return result;
  }, [runs, activeAiAgentId, statusFilter, searchQuery]);

  // Compute aggregate stats across agent runs - O(N)
  const stats = useMemo(() => {
    const agentRuns = runs.filter(r => String(r.ai_agent_id) === String(activeAiAgentId));
    if (agentRuns.length === 0) {
      return { avgAccuracy: 0, avgLatency: 0, avgSuccessRate: 0, totalTokens: 0 };
    }
    return {
      avgAccuracy: Math.round(agentRuns.reduce((acc, r) => acc + (r.summary?.avg_accuracy || 0), 0) / agentRuns.length),
      avgLatency: Math.round(agentRuns.reduce((acc, r) => acc + (r.summary?.avg_latency_ms || 0), 0) / agentRuns.length),
      avgSuccessRate: Math.round(agentRuns.reduce((acc, r) => acc + (r.summary?.success_rate || 0), 0) / agentRuns.length),
      totalTokens: agentRuns.reduce((acc, r) => acc + (r.summary?.total_tokens || 0), 0)
    };
  }, [runs, activeAiAgentId]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Model Evaluations
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Audit agent accuracy, reliability, and latency using Agno Judge-As-Agent metrics.
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            disabled={runningSuite}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-bold text-xs shadow-xs active:scale-[0.98] cursor-pointer self-start sm:self-auto disabled:opacity-50"
          >
            {runningSuite ? (
              <>
                <Activity className="animate-spin w-3.5 h-3.5" />
                <span>Evaluating...</span>
              </>
            ) : (
              <>
                <Play size={13} />
                <span>Run Evaluations</span>
              </>
            )}
          </button>
        </div>
      {/* Aggregate Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        {/* Judged Accuracy */}
        <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Judged Accuracy</span>
            <CheckCircle className="text-zinc-500 shrink-0" size={18} />
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">{stats.avgAccuracy}%</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Target criteria match</div>
          </div>
        </div>

        {/* Success Rate */}
        <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Success Rate</span>
            <Activity className="text-zinc-500 shrink-0" size={18} />
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">{stats.avgSuccessRate}%</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Test cases passed</div>
          </div>
        </div>

        {/* Latency */}
        <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Avg Latency</span>
            <Clock className="text-zinc-500 shrink-0" size={18} />
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">{stats.avgLatency} ms</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Model response time</div>
          </div>
        </div>

        {/* Total Tokens */}
        <div className="p-4 rounded-xl border border-zinc-200/80 bg-zinc-50/50 hover:border-zinc-300 transition-colors flex flex-col justify-between">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider truncate">Total Tokens</span>
            <Cpu className="text-zinc-500 shrink-0" size={18} />
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-zinc-900 tracking-tight">{stats.totalTokens}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">Tokens consumed</div>
          </div>
        </div>
      </div>

      {/* Main Grid Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left Side: Test Run History list */}
        <div className="lg:col-span-1 bg-white border border-zinc-200 rounded-xl shadow-2xs overflow-hidden flex flex-col h-[580px]">
          <div className="p-3.5 border-b border-zinc-100 bg-zinc-50/50 space-y-2.5">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-zinc-600 uppercase tracking-wider font-semibold">Evaluation History</h3>
              <span className="text-[10px] font-bold text-zinc-400">
                {filteredRuns.length} {filteredRuns.length === 1 ? 'Run' : 'Runs'}
              </span>
            </div>

            {/* Search Input */}
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Filter by run ID or prompt..."
                className="w-full pl-7 pr-3 py-1.5 bg-white border border-zinc-200 rounded-lg text-xs placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-900 transition-all"
              />
            </div>

            {/* Status Filter Tabs */}
            <div className="flex items-center gap-1.5">
              {['ALL', 'PASS', 'FAIL'].map((filter) => (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setStatusFilter(filter)}
                  className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold tracking-wide transition-colors cursor-pointer ${
                    statusFilter === filter
                      ? 'bg-zinc-900 text-white shadow-2xs'
                      : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                  }`}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-zinc-100">
            {loading && filteredRuns.length === 0 ? (
              <div className="p-8 text-center text-zinc-400 text-xs font-bold uppercase tracking-wider font-semibold">Loading runs...</div>
            ) : filteredRuns.length === 0 ? (
              <div className="p-8 text-center text-zinc-400 text-xs font-bold uppercase tracking-wider font-semibold">
                {searchQuery || statusFilter !== 'ALL' ? 'No matching evaluation runs' : 'No evaluation runs yet'}
              </div>
            ) : (
              filteredRuns.map(run => {
                const isSelected = selectedRun && selectedRun.id === run.id;
                const isPass = (run.summary?.status || '').toUpperCase() === 'PASS';
                return (
                  <button
                    key={run.id}
                    onClick={() => setSelectedRun(run)}
                    className={`w-full text-left p-4 hover:bg-zinc-50 transition-all flex items-center justify-between cursor-pointer ${
                      isSelected ? 'bg-zinc-50/80 border-l-4 border-zinc-900' : ''
                    }`}
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-zinc-800 uppercase tracking-tight">{run.id}</span>
                        <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded-lg ${
                          isPass ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/50' : 'bg-rose-50 text-rose-700 border border-rose-200/50'
                        }`}>
                          {run.summary?.status || 'UNKNOWN'}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-[10px] text-zinc-400 font-bold">
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          {new Date(run.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        <span>•</span>
                        <span>{run.summary?.passed || 0}/{run.summary?.total || 0} Passed</span>
                      </div>
                    </div>
                    <ChevronRight size={14} className="text-zinc-400" />
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Side: Selected Run Details Inspector */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl shadow-2xs overflow-hidden flex flex-col h-[580px]">
          {selectedRun ? (
            <>
              {/* Details Header */}
              <div className="p-5 border-b border-zinc-100 bg-zinc-50/50 flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold text-zinc-800 uppercase tracking-wider font-semibold flex items-center gap-2">
                    Run ID: {selectedRun.id}
                  </h3>
                  <p className="text-[10px] text-zinc-400 font-bold mt-0.5">
                    Executed on {new Date(selectedRun.timestamp).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <span className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Run Score</span>
                    <p className="text-xs font-bold text-zinc-900">{selectedRun.summary.avg_accuracy}% Accuracy</p>
                  </div>
                </div>
              </div>

              {/* Cases List */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                {selectedRun.cases.map((caseItem, idx) => (
                  <div key={idx} className="border border-zinc-200 rounded-xl p-5 space-y-4 hover:border-zinc-200 transition-all bg-zinc-50/20">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-xs font-bold text-zinc-800 uppercase tracking-wider">{caseItem.name}</h4>
                          <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded text-[9px] uppercase font-bold">
                            {caseItem.eval_type || 'judge'}
                          </span>
                        </div>
                        <span className="text-[9px] font-bold text-zinc-400 uppercase">Latency: {caseItem.duration_seconds}s</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-lg ${
                          caseItem.passed ? 'bg-emerald-50 text-emerald-700 border border-emerald-200/50' : 'bg-rose-50 text-rose-700 border border-rose-200/50'
                        }`}>
                          {caseItem.passed ? 'PASSED' : 'FAILED'}
                        </span>
                        <span className="text-xs font-bold px-2.5 py-0.5 bg-zinc-100 text-zinc-900 rounded-lg">
                          {caseItem.judge_score}/10
                        </span>
                      </div>
                    </div>

                    {/* Dialogue Bubbles */}
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1 shadow-2xs">
                        <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">Prompt Input</span>
                        <p className="text-zinc-700 font-medium">{caseItem.prompt}</p>
                      </div>
                      <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1 shadow-2xs">
                        <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold">
                          {caseItem.eval_type === 'reliability' ? 'Expected Tool Calls' : caseItem.eval_type === 'accuracy' ? 'Expected Response Text' : 'Expected Criteria'}
                        </span>
                        <p className="text-zinc-600 italic font-medium">
                          {caseItem.eval_type === 'reliability' ? (caseItem.expected_tool_calls?.join(', ') || 'None') : caseItem.expected}
                        </p>
                      </div>
                    </div>

                    <div className="p-3.5 bg-white border border-zinc-200 rounded-xl space-y-1.5 shadow-2xs text-xs">
                      <span className="text-[9px] font-bold text-zinc-400 uppercase tracking-wider font-semibold block">Actual Model Response</span>
                      <p className="text-zinc-800 font-bold leading-relaxed">{caseItem.actual}</p>
                    </div>

                    {/* Judge Agent Reasoning */}
                    <div className="p-3.5 bg-zinc-50 border border-zinc-200 rounded-xl space-y-1 text-xs">
                      <span className="text-[9px] font-bold text-zinc-900 uppercase tracking-wider font-semibold flex items-center gap-1">
                        <CheckCircle size={11} className="text-zinc-900" />
                        Judge Agent Verification Reasoning
                      </span>
                      <p className="text-zinc-800 font-bold leading-relaxed">{caseItem.judge_reason}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-zinc-400">
              <Info size={36} className="text-zinc-300 mb-3" />
              <p className="text-xs font-bold uppercase tracking-wider font-semibold">Select a test run to inspect grading details</p>
            </div>
          )}
        </div>
      </div>
      {/* Provisioning Modal */}
      {showAddModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-zinc-900/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4 font-['Inter',sans-serif] animate-fadeIn">
          <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl shadow-zinc-900/20 animate-slideIn border border-zinc-200/90 overflow-hidden">
            
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center bg-white">
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-zinc-900 tracking-tight">Configure Evaluation Cases</h3>
                <p className="text-xs text-zinc-500 mt-0.5">Provisioning & Test Protocol</p>
              </div>
              <button 
                onClick={() => setShowAddModal(false)} 
                className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                aria-label="Close dialog"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-5 sm:p-6 space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
              
              {/* Add New Case Form */}
              <div className="p-4 bg-zinc-50/60 border border-zinc-200/80 rounded-xl space-y-3">
                <h4 className="text-xs font-semibold text-zinc-800">Add New Test Case</h4>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-zinc-700 block mb-1">
                        Case Name *
                      </label>
                      <input
                        type="text"
                        value={newCase.name}
                        onChange={e => setNewCase({...newCase, name: e.target.value})}
                        placeholder="e.g. Greeting Test"
                        className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-900 text-zinc-900 placeholder:text-zinc-400 text-xs transition-all"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-zinc-700 block mb-1">
                        Evaluation Type
                      </label>
                      <CustomDropdown
                        options={[
                          { value: 'judge', label: 'Agent as Judge (Criteria)' },
                          { value: 'accuracy', label: 'Accuracy (Expected Answer)' },
                          { value: 'reliability', label: 'Reliability (Tool Calls)' }
                        ]}
                        value={newCase.eval_type}
                        onChange={val => setNewCase({...newCase, eval_type: val})}
                        className="w-full"
                        buttonClassName="bg-white border border-zinc-200 rounded-lg py-2 px-3 focus:ring-1 focus:ring-zinc-900 font-medium text-xs text-zinc-900 hover:bg-zinc-50/50"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-zinc-700 block mb-1">
                      User Input Prompt *
                    </label>
                    <input
                      type="text"
                      value={newCase.prompt}
                      onChange={e => setNewCase({...newCase, prompt: e.target.value})}
                      placeholder="What prompt should the test case send to the agent?"
                      className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-900 text-zinc-900 placeholder:text-zinc-400 text-xs transition-all"
                    />
                  </div>

                  {newCase.eval_type !== 'reliability' ? (
                    <div>
                      <label className="text-xs font-medium text-zinc-700 block mb-1">
                        {newCase.eval_type === 'accuracy' ? 'Expected Response Text *' : 'Expected Verification Criteria *'}
                      </label>
                      <input
                        type="text"
                        value={newCase.expected}
                        onChange={e => setNewCase({...newCase, expected: e.target.value})}
                        placeholder={newCase.eval_type === 'accuracy' ? "e.g. Hello, welcome to EasyChat! How can I help?" : "e.g. Must greet politely and ask how to help"}
                        className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-900 text-zinc-900 placeholder:text-zinc-400 text-xs transition-all"
                      />
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <MultiSelect
                        label="Expected Tool Calls"
                        value={newCase.expected_tool_calls}
                        options={(() => {
                          const toolOptionsMap = new Map();
                          if (humanEscalationEnabled) {
                            toolOptionsMap.set('request_human_assistant', { value: 'request_human_assistant', label: 'request_human_assistant' });
                          }
                          availableTools.forEach(t => {
                            if (t.name) {
                              toolOptionsMap.set(t.name, { value: t.name, label: t.name });
                            }
                          });
                          return Array.from(toolOptionsMap.values());
                        })()}
                        onChange={val => setNewCase({...newCase, expected_tool_calls: val})}
                      />
                    </div>
                  )}
                </div>

                <div className="pt-1 flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddCase}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-zinc-900 hover:bg-zinc-800 active:bg-black text-white rounded-lg font-medium text-xs transition-colors shadow-xs"
                  >
                    <Plus size={13} />
                    <span>Add Test Case</span>
                  </button>
                </div>
              </div>

              {/* Active Cases List */}
              <div className="space-y-2.5">
                <h4 className="text-xs font-semibold text-zinc-800">Active Test Cases ({testCases.length})</h4>
                <div className="border border-zinc-200 rounded-xl overflow-hidden divide-y divide-zinc-100 bg-white">
                  {testCases.map((tc, idx) => (
                    <div key={idx} className="p-3.5 flex items-center justify-between hover:bg-zinc-50/50 transition-colors text-xs">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-900 font-semibold text-xs">{tc.name}</span>
                          <span className="px-1.5 py-0.5 bg-zinc-100 text-zinc-600 rounded text-[10px] font-medium">
                            {tc.eval_type || 'judge'}
                          </span>
                        </div>
                        <p className="text-zinc-600">Prompt: <span className="font-normal text-zinc-800">{tc.prompt}</span></p>
                        {tc.eval_type !== 'reliability' ? (
                          <p className="text-zinc-400">Expected: <span className="font-normal italic text-zinc-600">{tc.expected}</span></p>
                        ) : (
                          <p className="text-zinc-400">Tools: <span className="font-normal font-mono text-zinc-600">{tc.expected_tool_calls?.join(', ') || 'None'}</span></p>
                        )}
                      </div>
                      <button 
                        type="button"
                        onClick={() => handleRemoveCase(tc.id)}
                        className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                        aria-label="Remove case"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  ))}
                  {testCases.length === 0 && (
                    <div className="p-6 text-center text-zinc-400 text-xs font-medium">No test cases added yet.</div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleRunEvaluations}
                disabled={testCases.length === 0 || runningSuite}
                className="w-full py-2.5 bg-zinc-900 text-white font-medium text-xs rounded-xl hover:bg-zinc-800 active:bg-black transition-colors shadow-xs flex items-center justify-center gap-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {runningSuite ? (
                  <>
                    <Activity className="animate-spin w-4 h-4 text-white" />
                    <span>Evaluating Suite...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle size={15} />
                    <span>Confirm & Execute Suite</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}  </div>
    </div>
  );
}
